import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { APP_STATUS, memberApi, useMemberMutation, errorMessage } from "@/hooks/useMember";
import TrialTracker from "@/components/member/TrialTracker";

const toneCls = {
  sage: "bg-sage/30 text-forest",
  champagne: "bg-champagne/25 text-forest",
  forest: "bg-forest text-ivory",
  muted: "bg-linen text-charcoal/60",
};

const fmt = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const inputCls = "w-full bg-cream border border-forest/20 px-4 py-3 text-sm text-charcoal focus:border-forest outline-none transition-colors";

function ShippingForm({ app, onClose }) {
  const [f, setF] = useState({ full_name: "", line1: "", line2: "", city: "", postcode: "", country: "", phone: "" });
  const accept = useMemberMutation(memberApi.accept, () => { toast.success("Invitation accepted. Your trial is being prepared."); onClose(); });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const required = ["full_name", "line1", "city", "postcode", "country"];
  const submit = (e) => {
    e.preventDefault();
    if (required.some((k) => !f[k].trim())) { toast.error("Please complete the required shipping fields."); return; }
    accept.mutate({ id: app.application_id, shipping: f }, { onError: (err) => toast.error(errorMessage(err)) });
  };
  return (
    <form onSubmit={submit} className="mt-6 border border-forest/15 bg-cream p-6 space-y-4" data-testid="shipping-form">
      <p className="eyebrow">Shipping details</p>
      <p className="text-sm text-charcoal/65 font-light">Used only to dispatch this programme's product. Shipping terms are shown on the programme page.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <input placeholder="Full name *" value={f.full_name} onChange={set("full_name")} className={inputCls} data-testid="ship-full-name" />
        <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={inputCls} data-testid="ship-phone" />
        <input placeholder="Address line 1 *" value={f.line1} onChange={set("line1")} className={`${inputCls} sm:col-span-2`} data-testid="ship-line1" />
        <input placeholder="Address line 2" value={f.line2} onChange={set("line2")} className={`${inputCls} sm:col-span-2`} data-testid="ship-line2" />
        <input placeholder="City *" value={f.city} onChange={set("city")} className={inputCls} data-testid="ship-city" />
        <input placeholder="Postcode *" value={f.postcode} onChange={set("postcode")} className={inputCls} data-testid="ship-postcode" />
        <input placeholder="Country *" value={f.country} onChange={set("country")} className={`${inputCls} sm:col-span-2`} data-testid="ship-country" />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button type="submit" disabled={accept.isPending} className="btn btn-primary disabled:opacity-60" data-testid="ship-submit">{accept.isPending ? "Saving…" : "Accept & save details"}</button>
        <button type="button" onClick={onClose} className="btn btn-outline">Cancel</button>
      </div>
    </form>
  );
}

export default function ApplicationCard({ app }) {
  const [shipping, setShipping] = useState(false);
  const meta = APP_STATUS[app.status] || { label: app.status, tone: "muted", text: "" };
  const withdraw = useMemberMutation(memberApi.withdraw, () => toast.success("You've withdrawn from this programme."));
  const decline = useMemberMutation(memberApi.decline, () => toast("Invitation declined."));
  const canWithdraw = ["submitted", "under_review", "waitlisted", "selected", "accepted"].includes(app.status) && !(app.trial && app.trial.status === "in_progress");

  return (
    <article className="border border-forest/15 bg-cream" data-testid={`application-card-${app.application_id}`} data-status={app.status}>
      <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-start justify-between gap-5">
        <div className="flex gap-5">
          {app.product?.image && (
            <div className="hidden sm:block w-16 h-20 bg-linen overflow-hidden shrink-0"><img src={app.product.image} alt="" className="w-full h-full object-contain p-1.5 mix-blend-multiply" /></div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">{app.product?.name} · Applied {fmt(app.created_at)}</p>
            <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-forest font-light leading-tight">
              <Link to={`/programmes/${app.programme?.slug}`} className="hover:text-olive transition-colors">{app.programme?.title}</Link>
            </h3>
            <p className="mt-2 text-sm text-charcoal/65 font-light max-w-lg">{meta.text}</p>
          </div>
        </div>
        <span className={`self-start shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] ${toneCls[meta.tone]}`} data-testid="application-status">{meta.label}</span>
      </div>

      {app.status === "selected" && !shipping && (
        <div className="px-6 sm:px-8 pb-6 flex flex-col sm:flex-row gap-3">
          <button onClick={() => setShipping(true)} className="btn btn-primary" data-testid="accept-invitation-button">Accept invitation <ArrowRight size={14} /></button>
          <button onClick={() => decline.mutate(app.application_id, { onError: (e) => toast.error(errorMessage(e)) })} className="btn btn-outline" data-testid="decline-invitation-button">Decline</button>
        </div>
      )}
      {shipping && <div className="px-6 sm:px-8 pb-8"><ShippingForm app={app} onClose={() => setShipping(false)} /></div>}

      {app.trial && <div className="px-6 sm:px-8 pb-8"><TrialTracker trial={app.trial} /></div>}

      <details className="border-t border-forest/10 px-6 sm:px-8 py-4 text-sm">
        <summary className="cursor-pointer text-[11px] uppercase tracking-[0.22em] text-charcoal/55 hover:text-forest">History</summary>
        <ol className="mt-4 space-y-2">
          {app.timeline.map((t) => (
            <li key={`${t.at}-${t.status}`} className="flex gap-4 text-charcoal/65 font-light"><span className="w-28 shrink-0 text-charcoal/45">{fmt(t.at)}</span>{APP_STATUS[t.status]?.label || t.status}{t.note && <span className="italic"> — {t.note}</span>}</li>
          ))}
        </ol>
        {canWithdraw && (
          <button onClick={() => { if (window.confirm("Withdraw from this programme? This cannot be undone.")) withdraw.mutate(app.application_id, { onError: (e) => toast.error(errorMessage(e)) }); }} className="mt-5 text-[11px] uppercase tracking-[0.22em] text-charcoal/50 hover:text-destructive link-underline" data-testid="withdraw-button">
            Withdraw application
          </button>
        )}
      </details>
    </article>
  );
}
